/**
 * A resolved skill set as a harness's own skill files (RFC 0334 3e).
 *
 * WHY. Claude Code has a skill system: it lists skills, loads one when it is
 * relevant, and lets you call one by name. Handed a text list instead, it
 * reads skills by a tool name it does not see (`read_document`, where it sees
 * `mcp__mod__read_document`). So Mod stays the authority on WHICH skills
 * exist, and hands them over in the form the harness was built to use.
 *
 * TRANSPORT ONLY. The files are rendered from the same set `resolveSkills`
 * returns, so they carry nothing the listed block does not. They are
 * rewritten each turn and belong to Mod; nobody edits them. A skill's source
 * stays its document, which each file names at the bottom.
 *
 * PURE. This renders paths and text; the host writes them, because core does
 * not touch a disk.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { readDocumentContent } from '../model/documents/reads'
import type { ProjectDoc } from '../model/project/types'
import { modSkillText } from './mod-skills'
import { readDocBounded, type SkillEntry } from './resolve-skills'

/** The plugin's name, which a harness shows in front of each skill (`mod:weekly`). */
export const NATIVE_SKILLS_PLUGIN = 'mod'

/** How long one skill document may take to arrive before it is left out. */
const BODY_READ_TIMEOUT_MS = 3000

/**
 * Each skill's text, by `skillId`. A Mod skill's comes from core, any other's
 * from its document. A document that does not arrive in time is left out
 * rather than holding up the turn; the skill is still written, with a line
 * saying where to read it.
 */
export async function readSkillBodies(
  repo: Repo,
  skills: readonly SkillEntry[],
  timeoutMs = BODY_READ_TIMEOUT_MS,
): Promise<Map<string, string>> {
  const bodies = new Map<string, string>()
  await Promise.all(
    skills.map(async (s) => {
      const mod = modSkillText(s.documentId)
      if (mod !== null) {
        bodies.set(s.skillId, mod)
        return
      }
      let timer: ReturnType<typeof setTimeout> | undefined
      try {
        const text = await Promise.race([
          (async () => {
            const h = await repo.find(s.documentId as AnyDocumentId)
            await (h as { whenReady?: () => Promise<void> }).whenReady?.()
            return readDocumentContent(h.doc())
          })(),
          new Promise<null>((resolve) => {
            timer = setTimeout(() => resolve(null), timeoutMs)
            ;(timer as { unref?: () => void }).unref?.()
          }),
        ])
        if (text?.trim()) bodies.set(s.skillId, text)
      } catch {
        // Unreachable document: written without a body, below.
      } finally {
        if (timer) clearTimeout(timer)
      }
    }),
  )
  return bodies
}

/** Supporting documents beside World and project skills' SKILL.md files.
 * The Mod documents remain canonical; these strings are disposable copies. */
export async function readSkillSupportFiles(
  repo: Repo,
  worldId: string | null | undefined,
  projectId: string | null | undefined,
  skills: readonly SkillEntry[],
): Promise<Map<string, Record<string, string>>> {
  const result = new Map<string, Record<string, string>>()
  const world = worldId ? await readDocBounded(repo, worldId) : null
  const homeId = (world as { homeProjectId?: unknown } | null)?.homeProjectId
  const [home, project] = await Promise.all([
    homeId ? readDocBounded(repo, String(homeId)) : null,
    projectId ? readDocBounded(repo, projectId) : null,
  ])
  for (const skill of skills.filter(
    (entry) => entry.scope === 'world' || entry.scope === 'project',
  )) {
    const owner = (
      skill.scope === 'world' ? home : project
    ) as ProjectDoc | null
    if (!owner) continue
    const prefix = `skills/${skill.skillId}/`
    const files: Record<string, string> = {}
    for (const [id, ref] of Object.entries(owner.documents ?? {})) {
      const path = String(ref.path ?? '')
      if (!path.startsWith(prefix)) continue
      const relative = path.slice(prefix.length)
      if (
        !relative ||
        relative === 'SKILL.md' ||
        relative
          .split('/')
          .some((part) => part === '.' || part === '..' || !part)
      )
        continue
      const snapshot = (await readDocBounded(repo, id)) as {
        data?: Record<string, unknown>
      } | null
      if (!snapshot) continue
      files[relative] = snapshot?.data
        ? `${JSON.stringify(snapshot.data, null, 2)}\n`
        : readDocumentContent(snapshot)
    }
    if (Object.keys(files).length) result.set(skill.skillId, files)
  }
  return result
}

/** A skill name a harness accepts: lowercase letters, digits and hyphens. */
export function nativeSkillName(skillId: string): string {
  return (
    skillId
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 64) || 'skill'
  )
}

/**
 * The plugin's files, by path relative to its root: a manifest, and one
 * `skills/<name>/SKILL.md` per skill with the `name` and `description`
 * frontmatter Claude Code reads.
 *
 * Frontmatter values are JSON strings, which YAML reads as double-quoted
 * scalars, so a description with a colon or a quote cannot break the file.
 */
export function renderNativeSkills(
  skills: readonly SkillEntry[],
  bodies: ReadonlyMap<string, string>,
  supportFiles: ReadonlyMap<string, Record<string, string>> = new Map(),
): Record<string, string> {
  const files: Record<string, string> = {
    '.claude-plugin/plugin.json': `${JSON.stringify(
      {
        name: NATIVE_SKILLS_PLUGIN,
        description:
          "Mod's skills for this project: Mod's own, the World's, the project's and its apps'. Written by Mod each turn; edit the documents, not these files.",
      },
      null,
      2,
    )}\n`,
  }
  const used = new Set<string>()
  for (const s of skills) {
    // Two ids that clean to one name (`foo!` and `foo`): the second gets a
    // number rather than being dropped, so the harness still has the whole set.
    // Dropping it would lose a skill silently, because a written plugin turns
    // the listed block off.
    const base = nativeSkillName(s.skillId)
    let name = base
    for (let n = 2; used.has(name); n++) {
      const suffix = `-${n}`
      name = `${base.slice(0, 64 - suffix.length)}${suffix}`
    }
    used.add(name)
    const body = bodies.get(s.skillId)?.trim()
    const source =
      s.scope === 'mod'
        ? 'This skill ships with Mod.'
        : `This skill is Mod document ${s.documentId}. To change it, edit that document.`
    files[`skills/${name}/SKILL.md`] =
      '---\n' +
      `name: ${JSON.stringify(name)}\n` +
      `description: ${JSON.stringify(s.description || s.title)}\n` +
      '---\n\n' +
      (body
        ? `${body}\n\n`
        : `The text did not arrive in time. Read document ${s.documentId} with Mod's read_document tool.\n\n`) +
      `${source}\n`
    for (const [relative, content] of Object.entries(
      supportFiles.get(s.skillId) ?? {},
    )) {
      files[`skills/${name}/${relative}`] = content
    }
  }
  return files
}
